/**
 * Telling a string apart from another string's overtone.
 *
 * Guitar tunings are built out of the harmonic series, so partials land on
 * each other exactly: E2's fourth partial *is* E4, and in DADGAD D3's second
 * *is* D4. Pluck the low E alone and the E4 meter lights up, because there
 * genuinely is energy at E4 — no filter can help, and neither can a frequency
 * ratio. Two perfectly harmonic tones an octave apart are indistinguishable
 * from one, by any method, in steady state.
 *
 * What does distinguish them is *how much* energy. A plucked string's partials
 * fall off smoothly with partial number, so the partials no other string sits
 * on say what the occupied ones should look like. Measure those, fit the
 * falloff, and predict the rest:
 *
 *   - measured ≈ predicted → the overtone explains it → ghost
 *   - measured ≫ predicted → something else is adding energy → a real string
 *
 * The fit is a straight line through log amplitude against log partial number,
 * which is the shape a plucked string actually has.
 */

/** One measured partial of one string. */
export interface PartialAmplitude {
  /** Partial number; 1 is the fundamental. */
  readonly partial: number
  /** Measured amplitude, linear. */
  readonly amplitude: number
  /** False when another string's fundamental sits on this partial. */
  readonly isFree: boolean
}

/** A fitted harmonic envelope: `amplitude ≈ exp(intercept) · n^slope`. */
export interface HarmonicEnvelope {
  readonly slope: number
  readonly intercept: number
  /** How many free partials the fit is based on. */
  readonly points: number
}

/** Smallest amplitude taken seriously; below it the logarithm is noise. */
export const AMPLITUDE_FLOOR = 1e-7

/** Free partials needed before a fit means anything. */
export const MIN_FIT_POINTS = 2

/**
 * How far above the prediction a partial has to sit to count as its own string.
 *
 * Measured over synthetic strums, ratio of measured to predicted:
 *
 * | case                                    | range      |
 * | --------------------------------------- | ---------- |
 * | pure ghost — E2 alone, read at B3 and E4 | 0.9 to 1.1 |
 * | real E4 sounding over E2's fourth partial | 8.5 to 43.8 |
 * | full strum, every string genuinely played | 3.8 to 15.4 |
 * | full strum with the high E at a third of the others | 2.5 to 6.1 |
 *
 * Two is the value with a factor of two of headroom on both sides. Higher
 * silences a quiet string that is really there, which is the worse mistake:
 * a ghost is visible and ignorable, a missing string makes tuning impossible.
 */
export const REAL_STRING_FACTOR = 2

/**
 * Fit the harmonic envelope of one string from the partials it owns alone.
 *
 * @param partials - Measured partials of a single string.
 * @returns The fit, or null when too few free partials carry any signal.
 */
export function fitEnvelope(partials: readonly PartialAmplitude[]): HarmonicEnvelope | null {
  const usable = partials.filter((p) => p.isFree && p.amplitude > AMPLITUDE_FLOOR && p.partial > 0)
  if (usable.length < MIN_FIT_POINTS) return null

  const xs = usable.map((p) => Math.log(p.partial))
  const ys = usable.map((p) => Math.log(p.amplitude))
  const meanX = xs.reduce((total, x) => total + x, 0) / xs.length
  const meanY = ys.reduce((total, y) => total + y, 0) / ys.length

  let covariance = 0
  let variance = 0
  for (let i = 0; i < xs.length; i += 1) {
    covariance += (xs[i]! - meanX) * (ys[i]! - meanY)
    variance += (xs[i]! - meanX) ** 2
  }

  // Every free partial at the same index would leave the slope undefined; a
  // flat envelope is the honest answer there.
  const slope = variance === 0 ? 0 : covariance / variance
  return { slope, intercept: meanY - slope * meanX, points: usable.length }
}

/** Amplitude the envelope predicts for a partial. */
export function predictAmplitude(envelope: HarmonicEnvelope, partial: number): number {
  return Math.exp(envelope.intercept + envelope.slope * Math.log(partial))
}

/** A string's fundamental, and which lower strings could account for it. */
export interface GhostQuery {
  /** Amplitude measured at the string's own fundamental. */
  readonly amplitude: number
  /**
   * For every lower string whose harmonic series lands here: that string's
   * partials, and which partial number coincides.
   */
  readonly explanations: readonly {
    readonly partial: number
    readonly partials: readonly PartialAmplitude[]
  }[]
}

/** What the model concluded about one string. */
export interface GhostVerdict {
  /** True when a lower string's overtone accounts for the whole reading. */
  readonly isGhost: boolean
  /** Largest amplitude any lower string's envelope predicts here. */
  readonly predicted: number
  /** Measured over predicted; Infinity when nothing predicts anything. */
  readonly ratio: number
}

/**
 * Decide whether a string is really sounding or is an overtone of a lower one.
 *
 * @param query - The string's own amplitude and the candidate explanations.
 * @param factor - How far above the prediction counts as a real string.
 */
export function judgeGhost(query: GhostQuery, factor = REAL_STRING_FACTOR): GhostVerdict {
  let predicted = 0

  for (const explanation of query.explanations) {
    const envelope = fitEnvelope(explanation.partials)
    if (!envelope) continue
    predicted = Math.max(predicted, predictAmplitude(envelope, explanation.partial))
  }

  if (predicted <= AMPLITUDE_FLOOR) {
    return { isGhost: false, predicted, ratio: Number.POSITIVE_INFINITY }
  }

  const ratio = query.amplitude / predicted
  return { isGhost: ratio < factor, predicted, ratio }
}
